import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Form, Button, FormControl, InputGroup, Modal, Table, Row, Col } from 'react-bootstrap';
import { FaTimes, FaCheck, FaPlus } from 'react-icons/fa';
import { getAllFarms } from '../../services/Farm/farmService';
import PaginationComponent from '../Pagination/PaginationComponent';
import CreateFarmModal from '../Farm/CreateFarmModal';

const FarmInput = ({ value, onChange, label, isInvalid, userRoles, createdNew }) => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedFarm, setSelectedFarm] = useState(value || { id: null, name: '' });
  const [searchText, setSearchText] = useState(value?.name || '');
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [farms, setFarms] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [filterParams, setFilterParams] = useState({});
  const previousFilterParams = useRef({});
  const [shouldFetch, setShouldFetch] = useState(false);

  useEffect(() => {
    setSelectedFarm(value || { id: null, name: '' });
    setSearchText(value?.name || '');
    setIsReadOnly(!!value?.id);
  }, [value]);

  const fetchFarms = useCallback(async () => {
    if (!shouldFetch) return;

    try {
      const data = await getAllFarms(filterParams, page - 1, pageSize);
      setFarms(data.content);
      setTotalPages(data.totalPages);
    } catch (error) {
      console.error('Erro ao buscar fazendas:', error);
    }
  }, [shouldFetch, filterParams, page, pageSize]);

  useEffect(() => {
    if (shouldFetch) {
      fetchFarms();
    }
  }, [page, pageSize, filterParams, fetchFarms, shouldFetch]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const filter = { name: searchText };
      handleFilter(filter);
      setShowModal(true);
    }
  };

  const handleSelectFarm = (farm) => {
    setSelectedFarm(farm);
    setSearchText(farm.name);
    setIsReadOnly(true);
    onChange({ name: farm.name, id: farm.id });
    setShowModal(false);
    setShouldFetch(false);
  };

  const handleClear = () => {
    setSelectedFarm({ id: null, name: '' });
    setSearchText('');
    setIsReadOnly(false);
    onChange(null);
  };

  const handleCloseModal = () => {
    if (!selectedFarm.id) {
      setSearchText('');
    }
    setShowModal(false);
    setShouldFetch(false);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    setShouldFetch(true);
  };

  const handleFilter = (filters) => {
    const filtersChanged = JSON.stringify(previousFilterParams.current) !== JSON.stringify(filters);

    if (filtersChanged) {
      setFilterParams(filters);
      setPage(1);
      previousFilterParams.current = filters;
      setShouldFetch(true);
    }
  };

  return (
    <>
      <div className="mb-3">
        {label && <Form.Label className="mb-1">{label}</Form.Label>}
        <InputGroup className={isInvalid ? 'is-invalid' : ''}>
          <FormControl
            placeholder="Selecione uma Fazenda"
            value={searchText}
            isInvalid={isInvalid}
            readOnly={isReadOnly}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={handleKeyDown}
            autoComplete='off'
          />
          <Button variant="outline-secondary" onClick={handleClear} disabled={userRoles}>
            <FaTimes />
          </Button>
        </InputGroup>
        {isInvalid && <div className="invalid-feedback">{label} é obrigatória.</div>}
      </div>

      <Modal show={showModal} onHide={handleCloseModal} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Selecionar Fazenda</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ textAlign: 'center', display: createdNew ? 'block' : 'none' }} className="mb-3">
            <Button
              variant="primary"
              className="button-custom mt-0"
              onClick={(e) => {
                e.stopPropagation();
                setShowModal(false);
                setShowCreateModal(true);
              }}
            >
              <FaPlus className="me-2" />
              Novo
            </Button>
          </div>

          <Table striped bordered hover responsive="sm" className="mt-3">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Documento</th>
                <th style={{ width: '50px' }}>Ação</th>
              </tr>
            </thead>
            <tbody>
              {farms.map((farm) => (
                <tr key={farm.id}>
                  <td>{farm.name}</td>
                  <td>{farm.document || '-'}</td>
                  <td>
                    <Button
                      variant="outline-primary"
                      onClick={() => handleSelectFarm(farm)}
                      className="btn-primary button-custom d-flex align-items-center justify-content-center p-1"
                      style={{ borderRadius: '4px', padding: '5px 10px', minWidth: '40px', height: '30px' }}
                    >
                      <FaCheck style={{ color: 'inherit', opacity: 0.6 }} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          <Row className="mt-3">
            <Col>
              <PaginationComponent
                totalPages={totalPages}
                currentPage={page}
                onPageChange={handlePageChange}
                totalElements={totalPages * pageSize}
                size={pageSize}
                pageNumber={page - 1}
                numberOfElements={farms.length}
              />
            </Col>
          </Row>
        </Modal.Body>
      </Modal>
      
      <CreateFarmModal
        show={showCreateModal}
        handleClose={() => setShowCreateModal(false)}
        onCreated={(created) => {
          setShowCreateModal(false);
          setSelectedFarm(created);
          setSearchText(created.name);
          setIsReadOnly(true);
          onChange(created);
        }}
      />
    </>
  );
};

export default FarmInput;